import { format } from 'date-fns'
import React from 'react'
import { formatPrice } from 'shared-constants'
import styled from 'styled-components'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import { Delimiter } from 'components/Delimiter'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Flex } from 'components/Layout'
import Link from 'components/Link'
import { LOADER_SIZE } from 'components/Loader'
import NoData from 'components/NoData'
import {
  SessionPreview,
  SessionDate,
  SessionInfo,
} from 'components/SessionPreview'
import { Text } from 'components/Typography'
import { WrappedLoader } from 'components/WrappedLoader'
import { SESSION_STATUSES } from 'constants/common'
import { DATE_DEFAULT } from 'constants/datetime'
import { ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import {
  SessionSorterField,
  SessionStatus,
  SortDirection,
  useExpertUpcomingSessionsQuery,
} from 'types/graphql-generated'

const StyledText = styled(Text)`
  display: flex;
`

const UPCOMING_SESSIONS_LIMIT = 3

export const UpcomingSessionsWidget = () => {
  const { user, convertDateToTimezone } = useUserAuth()
  const { data, loading: isLoading } = useExpertUpcomingSessionsQuery({
    variables: {
      input: {
        expertUserId: user.id,
        filter: {
          statuses: [SessionStatus.Pending, SessionStatus.OnHold],
        },
        pagination: {
          limit: UPCOMING_SESSIONS_LIMIT,
        },
        sorter: {
          field: SessionSorterField.StartAtUtc,
          direction: SortDirection.Asc,
        },
      },
    },
  })
  const {
    expertBookedSessions: {
      paginationInfo = {
        totalCount: undefined,
      },
      data: expertBookedSessionsData = [],
      stats: { totalPrice: totalPrices = [] } = {},
    } = {},
  } = data || {}

  return (
    <Card
      flexDirection="column"
      width="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
    >
      <Flex p={SPACE.PX_30} pb={SPACE.PX_20} flexDirection="column">
        <Flex>
          <StyledText
            fontWeight={FONT_WEIGHT.MEDIUM}
            fontSize={FONT_SIZE.PX_20}
            color={COLOR_INTENT.UPCOMING_SESSION_WIDGET.HEADER}
            mb={SPACE.PX_5}
          >
            Upcoming consultations{' '}
            {isLoading ? (
              <WrappedLoader
                isFillSpace={false}
                ml={SPACE.PX_5}
                loaderSize={LOADER_SIZE.XXS}
              />
            ) : (
              <Flex ml={SPACE.PX_5} flexShrink={0}>
                {`(${paginationInfo?.totalCount})`}
              </Flex>
            )}
          </StyledText>
        </Flex>
        <StyledText color={COLOR_INTENT.UPCOMING_SESSION_WIDGET.VALUE}>
          Total value{' '}
          {isLoading ? (
            <WrappedLoader
              isFillSpace={false}
              ml={SPACE.PX_5}
              loaderSize={LOADER_SIZE.XXS}
            />
          ) : (
            <Text
              ml={SPACE.PX_5}
              color={COLOR_INTENT.UPCOMING_SESSION_WIDGET.PRICE}
              fontWeight={FONT_WEIGHT.MEDIUM}
            >
              {totalPrices.length === 0
                ? formatPrice({
                    price: 0,
                  })
                : totalPrices
                    .map(({ base, currencyIsoCode }) =>
                      formatPrice({
                        price: base,
                        currency: currencyIsoCode,
                      })
                    )
                    .join(' + ')}
            </Text>
          )}
        </StyledText>
      </Flex>
      <Delimiter />
      <Flex flexDirection="column" height="100%" flex={1}>
        {isLoading ? (
          <WrappedLoader
            flex={1}
            mt={SPACE.PX_20}
            loaderSize={LOADER_SIZE.XS}
          />
        ) : (
          <Flex flexDirection="column" flex={1}>
            {expertBookedSessionsData.length === 0 ? (
              <Flex
                alignItems="center"
                flex={1}
                width="100%"
                justifyContent="center"
                minHeight="300px"
              >
                <NoData
                  header="No upcoming sessions"
                  content="Your upcoming consultations will appear here."
                />
              </Flex>
            ) : (
              expertBookedSessionsData.map((session) => {
                const {
                  sessionData: {
                    title,
                    lengthInMinutes,
                    sessionType,
                    price: { total, currencyIsoCode },
                  },
                  status,
                  startAtUtc,
                  endAtUtc,
                } = session
                const totalPrice = formatPrice({
                  price: total,
                  currency: currencyIsoCode,
                })
                const startDateObject = convertDateToTimezone(
                  new Date(startAtUtc.isoFormat)
                )
                const endDateObject = convertDateToTimezone(
                  new Date(endAtUtc.isoFormat)
                )
                return (
                  <Flex key={session.id} flexDirection="column">
                    <Flex px={SPACE.PX_30} py={SPACE.PX_20} width="100%">
                      <SessionPreview
                        hasDetail={false}
                        gridTemplateColumns={{
                          MOBILE: '1fr',
                          TABLET: '2fr 2fr 1fr',
                        }}
                      >
                        {status === SESSION_STATUSES.ON_HOLD ? (
                          <Text fontSize={FONT_SIZE.PX_16}>On Hold</Text>
                        ) : (
                          <SessionDate
                            startDateObject={startDateObject}
                            endDateObject={endDateObject}
                          />
                        )}
                        <SessionInfo
                          title={title}
                          price={totalPrice}
                          lengthInMinutes={lengthInMinutes}
                          sessionType={sessionType}
                        />
                        <Flex
                          key="link-to-upcoming-session"
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <Link
                            flexShrink={0}
                            href={ROUTE.EXPERT_CALENDAR_UPCOMING({
                              sessionId: session.id,
                              date: format(startDateObject, DATE_DEFAULT),
                            })}
                            aria-label="Upcoming consultations details"
                          >
                            <Text
                              color={
                                COLOR_INTENT.UPCOMING_SESSION_WIDGET.DETAILS
                              }
                              mr={SPACE.PX_10}
                              fontWeight={FONT_WEIGHT.SEMI_BOLD}
                            >
                              Details
                            </Text>
                            <Icon.CaretRight size={ICON_SIZE.PX_20} />
                          </Link>
                        </Flex>
                      </SessionPreview>
                    </Flex>
                    <Delimiter />
                  </Flex>
                )
              })
            )}
          </Flex>
        )}
      </Flex>
      {expertBookedSessionsData.length !== 0 && (
        <Flex px={SPACE.PX_30} py={SPACE.PX_20}>
          <Link
            href={ROUTE.EXPERT_CALENDAR_UPCOMING()}
            aria-label="See all in showroom calendar"
          >
            See all
          </Link>
        </Flex>
      )}
    </Card>
  )
}
